import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { MapPin, Building2, Check, Navigation, Search } from 'lucide-react';
import { useStore } from '@/contexts/StoreContext';
import { useTheme } from '@/contexts/ThemeContext';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';

interface StoreSelectionModalProps {
  config: any;
  isOpen?: boolean;
  onClose?: () => void;
}

export const StoreSelectionModal = ({ config, isOpen: externalIsOpen, onClose: externalOnClose }: StoreSelectionModalProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { availableStores, selectedStore, setSelectedStoreById } = useStore();
  const { colors } = useTheme();
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;

  // Use external control if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalOnClose !== undefined 
    ? (open: boolean) => !open && externalOnClose() 
    : setInternalIsOpen;

  // Request user location when modal opens
  useEffect(() => {
    if (isOpen && !userLocation) {
      const getLocation = async () => {
        const location = await requestUserLocation();
        if (location) {
          setUserLocation(location);
        }
      };
      getLocation();
    }
  }, [isOpen]);

  useEffect(() => {
    const checkAndShowModal = async () => {
      // Check if user has accepted/rejected cookies first
      const cookieConsent = await secureGetItem('cookieConsent');
      
      if (!cookieConsent) {
        return; // Wait for cookie consent
      }

      // Check if suggestStoreSelection is set
      const suggestStoreId = config?.suggestStoreSelection;
      
      if (!suggestStoreId || suggestStoreId === null) {
        return; // Don't show modal
      }

      // Check if user has already seen the modal
      const hasSeenModal = await secureGetItem('store-selection-shown');
      
      if (hasSeenModal) {
        return; // Don't show again
      }

      // Check if there are multiple stores
      if (availableStores.length <= 1) {
        return; // No need to show if only one store
      }

      // Check if current selected store is different from suggested
      if (selectedStore?._id !== suggestStoreId) {
        setInternalIsOpen(true);
        setHasShown(true);
        // Mark as shown
        await secureSetItem('store-selection-shown', true);
        
        // Request user location for distance calculation
        const location = await requestUserLocation();
        if (location) {
          setUserLocation(location);
        }
      }
    };

    // Only auto-show if not externally controlled
    if (externalIsOpen === undefined && config && availableStores.length > 0 && !hasShown) {
      checkAndShowModal();
    }

    // Listen for cookie consent changes
    const handleCookieConsent = () => {
      if (!hasShown) {
        checkAndShowModal();
      }
    };

    window.addEventListener('cookieConsentUpdated', handleCookieConsent);
    return () => window.removeEventListener('cookieConsentUpdated', handleCookieConsent);
  }, [config, availableStores, selectedStore, hasShown]);

  const handleStoreSelect = async (storeId: string) => {
    await setSelectedStoreById(storeId);
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Filter and sort stores by distance (if user location is available)
  const sortedStores = useMemo(() => {
    // Filter by search term
    let filtered = availableStores;
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = availableStores.filter(store => {
        const companyMatch = store.company?.toLowerCase().includes(term);
        const addressMatch = store.place?.address?.toLowerCase().includes(term);
        const cityMatch = store.place?.city?.toLowerCase().includes(term);
        return companyMatch || addressMatch || cityMatch;
      });
    }

    // Sort by distance if available
    if (!userLocation) {
      return filtered;
    }

    return [...filtered].sort((a, b) => {
      const distanceA = getDistanceToStore(userLocation, a);
      const distanceB = getDistanceToStore(userLocation, b);
      
      // Put stores without location at the end
      if (distanceA === null) return 1;
      if (distanceB === null) return -1;
      
      return distanceA - distanceB;
    });
  }, [availableStores, userLocation, searchTerm]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="w-full h-full md:max-w-2xl md:h-[80vh] p-0 flex flex-col gap-0 overflow-hidden [&>button]:hidden">
        {/* Fixed Header */}
        <DialogHeader className="sticky top-0 z-10 bg-background border-b px-4 md:px-6 py-3 md:py-4 rounded-t-lg space-y-3">
          <div>
            <DialogTitle className="text-lg md:text-2xl font-bold flex items-center gap-2">
              <Building2 className="h-5 w-5 md:h-6 md:w-6" style={{ color: primaryColor }} />
              <span className="line-clamp-1">Selecione a unidade de sua preferência</span>
            </DialogTitle>
            <p className="text-xs md:text-sm text-muted-foreground mt-1 md:mt-2">
              Você pode alterar essa escolha a qualquer momento no topo do site.
            </p>
          </div>

          {/* Search Filter */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou endereço..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-10 text-sm"
            />
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-3 md:py-4">
          {sortedStores.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground font-medium">
                Nenhuma loja encontrada
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Tente buscar por outro termo
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedStores.map(store => {
              const isSelected = selectedStore?._id === store._id;
              const isSuggested = store._id === config?.suggestStoreSelection;
              const distance = getDistanceToStore(userLocation, store);
              
              return (
                <Card
                  key={store._id}
                  className={`cursor-pointer transition-all duration-300 hover:shadow-md ${
                    isSelected 
                      ? 'ring-2 ring-offset-2' 
                      : 'hover:border-primary/50'
                  }`}
                  style={isSelected ? { ringColor: primaryColor } : {}}
                  onClick={() => handleStoreSelect(store._id)}
                >
                  <div className="p-3 md:p-4 flex items-center gap-3 md:gap-4">
                    {/* Selected Check or Suggested Badge */}
                    <div className="flex-shrink-0">
                      {isSelected ? (
                        <div 
                          className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <Check className="h-4 w-4 md:h-5 md:w-5" />
                        </div>
                      ) : isSuggested ? (
                        <div 
                          className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white text-xs font-bold"
                          style={{ backgroundColor: primaryColor }}
                        >
                          ★
                        </div>
                      ) : (
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-muted flex items-center justify-center">
                          <Building2 className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Store Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-sm md:text-base truncate">
                          {store.company}
                        </h3>
                        {isSuggested && (
                          <span 
                            className="text-[10px] md:text-xs font-bold px-1.5 md:px-2 py-0.5 rounded-full text-white flex-shrink-0"
                            style={{ backgroundColor: primaryColor }}
                          >
                            Sugerida
                          </span>
                        )}
                      </div>

                      {/* Address */}
                      {store.place?.address && (
                        <div className="flex items-start gap-1 md:gap-1.5 text-xs md:text-sm text-muted-foreground mb-1">
                          <MapPin className="h-3 w-3 md:h-3.5 md:w-3.5 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-1">{store.place.address}</span>
                        </div>
                      )}

                      {/* Phone */}
                      {store.phone?.number && (
                        <div className="text-xs md:text-sm text-muted-foreground">
                          📞 {store.phone.number}
                        </div>
                      )}
                    </div>

                    {/* Distance Badge + Arrow */}
                    <div className="flex-shrink-0 flex items-center gap-1 md:gap-2">
                      {distance !== null && (
                        <Badge 
                          variant="secondary" 
                          className="flex items-center gap-0.5 md:gap-1 px-1.5 md:px-2 py-0.5 md:py-1 text-white font-medium"
                          style={{ backgroundColor: secondaryColor }}
                        >
                          <Navigation className="h-2.5 w-2.5 md:h-3 md:w-3" />
                          <span className="text-[10px] md:text-xs">{formatDistance(distance)}</span>
                        </Badge>
                      )}
                      
                      <svg 
                        className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Card>
              );
            })}
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="sticky bottom-0 z-10 bg-background border-t px-6 py-4 rounded-b-lg">
          <Button 
            variant="outline" 
            onClick={handleClose}
            className="w-full"
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

